/**
 * Resolves product content from the CMS, falling back to the static registry.
 *
 * The CMS is the source of truth so the team can change product copy without a
 * deploy. Two things can go wrong with that, and they need different answers:
 *
 *   - the API is unreachable  -> render the static registry, the page is fine
 *   - the API answers, but a record is missing fields the page renders
 *     -> ALSO render the static registry for that record
 *
 * The second case is the one worth being strict about. A partially populated
 * product produces a page with empty sections, which reads as a broken product
 * rather than a broken fetch — and it ships to a buyer either way. So a CMS
 * record is used only when it carries everything a page needs; otherwise the
 * static entry wins for that product alone, and the rest still come from the CMS.
 */

import { getPlatformProducts } from "./api";
import {
    PRODUCTS,
    PRODUCT_CATEGORIES,
    ProductItem,
    ProductCategory,
    ProductFeature,
    ProductMetric,
    getProductBySlug,
} from "../data/productsData";

const isNonEmptyString = (v: unknown): v is string =>
    typeof v === "string" && v.trim().length > 0;

const asFeatures = (raw: unknown): ProductFeature[] =>
    Array.isArray(raw)
        ? raw
              .filter((f) => f && isNonEmptyString(f.title))
              .map((f) => ({ title: f.title, description: f.description ?? "" }))
        : [];

const asMetrics = (raw: unknown): ProductMetric[] =>
    Array.isArray(raw)
        ? raw
              .filter((m) => m && isNonEmptyString(m.value))
              .map((m) => ({ value: m.value, label: m.label ?? "" }))
        : [];

const asStrings = (raw: unknown): string[] =>
    Array.isArray(raw) ? raw.filter(isNonEmptyString) : [];

let cachedProducts: ProductItem[] | null = null;
let pendingFetch: Promise<ProductItem[]> | null = null;

export const getCachedProducts = (): ProductItem[] | null => cachedProducts;
export const getCachedProduct = (slug: string): ProductItem | undefined =>
    cachedProducts?.find((p) => p.slug === slug);

/**
 * Maps a CMS API record onto ProductItem.
 * When the API provides data, API data takes 100% priority.
 * Local static data is only used as a fallback for missing fields.
 */
function fromCms(raw: any): ProductItem | null {
    if (!raw || !isNonEmptyString(raw.slug)) {
        return null;
    }

    const fallback = getProductBySlug(raw.slug);

    const category = raw.category && PRODUCT_CATEGORIES.includes(raw.category)
        ? (raw.category as ProductCategory)
        : (fallback?.category ?? "AI Agents");

    return {
        slug: String(raw.slug).trim(),
        name: isNonEmptyString(raw.name) ? raw.name.trim() : (isNonEmptyString(raw.title) ? raw.title.trim() : (fallback?.name ?? "")),
        category,
        tagline: typeof raw.tagline === "string" ? raw.tagline : (fallback?.tagline ?? ""),
        summary: typeof raw.summary === "string" ? raw.summary : (typeof raw.description === "string" ? raw.description : (fallback?.summary ?? "")),
        problem: typeof raw.problem === "string" ? raw.problem : (raw.problemStatement?.title ?? fallback?.problem ?? ""),
        solution: typeof raw.solution === "string" ? raw.solution : (raw.solutionStatement ?? fallback?.solution ?? ""),
        features: Array.isArray(raw.features) ? asFeatures(raw.features) : (fallback?.features ?? []),
        metrics: Array.isArray(raw.metrics) ? asMetrics(raw.metrics) : (fallback?.metrics ?? []),
        architecture: Array.isArray(raw.architecture) ? asStrings(raw.architecture) : (fallback?.architecture ?? []),
        subdomain: typeof raw.subdomain === "string"
            ? (raw.subdomain.trim() || undefined)
            : fallback?.subdomain,
        hasSignIn: typeof raw.hasSignIn === "boolean"
            ? raw.hasSignIn
            : (fallback?.hasSignIn ?? false),
        accent: isNonEmptyString(raw.accent) ? raw.accent.trim() : (fallback?.accent ?? "#BED743"),
    };
}

/**
 * Loads all products: FIRST priority from API.
 * If the API is not working or unreachable, falls back to local static data.
 */
export async function loadProducts(): Promise<ProductItem[]> {
    if (pendingFetch) return pendingFetch;

    pendingFetch = (async () => {
        try {
            const raw = await getPlatformProducts();
            if (Array.isArray(raw) && raw.length > 0) {
                const apiProducts = raw
                    .filter((r) => r && isNonEmptyString(r.slug) && !r.isDeleted)
                    .map((r) => fromCms(r))
                    .filter((p): p is ProductItem => p !== null);

                if (apiProducts.length > 0) {
                    cachedProducts = apiProducts;
                    return apiProducts;
                }
            }
        } catch (err) {
            console.warn("CMS API unreachable, falling back to local static data:", err);
        } finally {
            pendingFetch = null;
        }

        return cachedProducts ?? PRODUCTS;
    })();

    return pendingFetch;
}

/**
 * Loads one product by slug: FIRST priority from API.
 * If the API is not working or unreachable, falls back to local static data.
 */
export async function loadProduct(slug: string): Promise<ProductItem | undefined> {
    try {
        const all = await loadProducts();
        const match = all.find((p) => p.slug === slug);
        if (match) return match;
        if (cachedProducts) {
            return undefined;
        }
    } catch (err) {
        console.warn("CMS API unreachable, falling back to local static data:", err);
    }

    return getProductBySlug(slug);
}
